import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { Tabs } from '@hills-design-system/components';
import Settings from './components/Settings';
import Teams from './components/Teams';
import { isTeamsIntegrationEnabled } from '../../utils/featureFlags';

const DEFAULT_TAB = 'settings';

const Account: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const teamsEnabled = isTeamsIntegrationEnabled();
  const isValidTab = (tab: string | null): boolean =>
    tab === 'settings' || (tab === 'teams' && teamsEnabled);
  const [activeTab, setActiveTab] = React.useState(
    isValidTab(tabParam) ? (tabParam as string) : DEFAULT_TAB,
  );
  const { t } = useTranslation();

  useEffect(() => {
    // If teams integration is disabled, force back to default tab
    if (!teamsEnabled && tabParam === 'teams') {
      setSearchParams({ tab: DEFAULT_TAB }, { replace: true });
      setActiveTab(DEFAULT_TAB);
      return;
    }
    // If the tab param changes externally, update the state
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    // If no tab param, set it to default
    if (!tabParam) {
      setSearchParams({ tab: DEFAULT_TAB });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  const tabItems = [
    { id: 'settings', label: t(TranslationKeyEnum.Settings) },
    ...(teamsEnabled ? [{ id: 'teams', label: t(TranslationKeyEnum.Teams) }] : []),
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'settings':
        return <Settings />;
      case 'teams':
        return <Teams />;
      default:
        return <Settings />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-grow">
        {/* Page Header */}
        <div className="pb-12">
          <h1 className="text-[64px] font-bold tracking-[-3%] text-[#192026] leading-[1.125em]">
            {t(TranslationKeyEnum.Account)}
          </h1>
        </div>

        {/* Tabs */}
        <Tabs items={tabItems} activeTab={activeTab} onChange={handleTabChange} />

        {/* Content */}
        <div className="py-8 flex flex-col items-center">{renderTabContent()}</div>
      </main>
    </div>
  );
};

export default Account;
